import {
  PROVINCIAL_LEADING_INDUSTRY_ALIASES,
  PROVINCIAL_LEADING_INDUSTRY_OPTIONS,
} from '../constants/parkFilters';
import {
  FilterCondition,
  NationalPark,
  Park,
  PendingPark,
  ProvincialLikePark,
  ProvincialPark,
} from '../types';
import {
  filterParksByCondition,
  isNationalPark,
  isPendingPark,
  isProvincialPark,
} from '../utils/helpers';
import {
  flattenLabeledMultiValues,
  splitLabeledMultiValue,
  splitMultiValueCell,
} from '../utils/splitMultiValue';

const LEADING_INDUSTRY_SPLIT = {
  knownLabels: PROVINCIAL_LEADING_INDUSTRY_OPTIONS,
  aliases: PROVINCIAL_LEADING_INDUSTRY_ALIASES,
} as const;

const API_BASE = (process.env.REACT_APP_API_URL ?? '').trim().replace(/\/$/, '');
const USE_REMOTE_API = Boolean(API_BASE);

// 直接换成 JSON 路径
const MOCK_NATIONAL_JSON = `${process.env.PUBLIC_URL ?? ''}/data/nationalParks.json`;
const MOCK_PROVINCIAL_JSON = `${process.env.PUBLIC_URL ?? ''}/data/provincialParks.json`;
const MOCK_PENDING_JSON = `${process.env.PUBLIC_URL ?? ''}/data/pending.json`;

let mockCache: {
  national: NationalPark[];
  provincial: ProvincialLikePark[];
} | null = null;

// 读取 JSON
async function fetchJson(url: string) {
  const res = await fetch(url, { cache: 'no-cache' });
  if (!res.ok) throw new Error(`请求失败 ${res.status}: ${url}`);
  return res.json();
}

function splitMultiCell(raw: unknown): string[] {
  return splitMultiValueCell(raw);
}

function normalizePendingApproval(raw: unknown): PendingPark['pendingApproval'] {
  const s = String(raw ?? '').trim();
  if (/国务院|中央|国家/.test(s)) return 'state_council';
  return 'provincial_government';
}

function normalizeIdPart(raw: unknown): string {
  return toTrimString(raw).replace(/\s+/g, ' ');
}

/**
 * 生成稳定园区 ID（与数组顺序无关，避免数据增删导致 parkId 漂移）
 */
function buildStableParkId(
  level: 'national' | 'provincial' | 'pending',
  parts: Array<unknown>,
): string {
  const normalized = parts
    .map((x) => normalizeIdPart(x))
    .filter(Boolean);
  return `${level}:${normalized.join('|')}`;
}

function isJsonRowPending(item: Record<string, unknown>): boolean {
  return String(item['园区等级'] ?? '').trim() === '待开发';
}

function mapJsonToProvincialLike(
  item: Record<string, unknown>,
  kind: 'provincial' | 'pending',
  pendingApproval?: PendingPark['pendingApproval'],
): ProvincialPark | PendingPark {
  const hasGreenElectricity = ['是', '有', 'true', '1'].includes(
    String(item['是否有绿电资源'] ?? '')
      .trim()
      .toLowerCase(),
  );
  const base = {
    name: String(item['园区名称'] ?? '').trim(),
    province: String(item['省份'] ?? '').trim(),
    city: String(item['市'] ?? '').trim(),
    district: String(item['区'] ?? '').trim(),
    file: String(item['文件'] ?? '').trim(),
    dockingStatus: String(item['对接情况'] ?? '').trim(),
    hasGreenElectricity,
    greenElectricityScale: String(item['绿电资源规模'] ?? '').trim(),
    leadingIndustries: splitLabeledMultiValue(
      item['主导产业类型'],
      LEADING_INDUSTRY_SPLIT,
    ),
    comprehensiveEnergyConsumption: String(
      item['综合能源消费量'] ?? '',
    ).trim(),
    basicSituation: String(item['园区基本情况'] ?? '').trim(),
    failReason: String(item['申报失败原因'] ?? '').trim(),
    remark: String(item['备注'] ?? '').trim(),
    longitude: Number(item['经度']) || 0,
    latitude: Number(item['纬度']) || 0,
  };
  if (kind === 'pending') {
    const approval = pendingApproval ?? 'state_council';
    return {
      parkLevel: 'pending',
      pendingApproval: approval,
      id: buildStableParkId('pending', [approval, base.province, base.city, base.district, base.name]),
      ...pickPendingGdpMetadata(item),
      ...base,
    };
  }
  return {
    parkLevel: 'provincial',
    id: buildStableParkId('provincial', [base.province, base.city, base.district, base.name]),
    ...base,
  };
}

function toTrimString(raw: unknown): string {
  if (raw == null) return '';
  return String(raw).trim();
}

function toNumberOrZero(raw: unknown): number {
  const s = toTrimString(raw);
  if (!s) return 0;
  const n = typeof raw === 'number' ? raw : Number(s);
  return Number.isFinite(n) ? n : 0;
}

function toNullableNumber(raw: unknown): number | null {
  if (raw == null || toTrimString(raw) === '') return null;
  const n = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(n) ? n : null;
}

function toNullableInteger(raw: unknown): number | null {
  const n = toNullableNumber(raw);
  return n == null ? null : Math.trunc(n);
}

function normalizeGdpVerificationStatus(
  raw: unknown,
): PendingPark['grossRegionalProductVerificationStatus'] {
  const value = toTrimString(raw);
  if (value === 'verified' || value === '已核验') return 'verified';
  if (value === 'official_estimate' || value === '官方预期值') return 'official_estimate';
  if (value === 'official_qualitative' || value === '官方定性表述') return 'official_qualitative';
  return 'not_found';
}

function pickPendingGdpMetadata(item: Record<string, unknown>) {
  return {
    grossRegionalProduct: toNullableNumber(
      item.grossRegionalProduct ??
        item['园区地区生产总值（亿元）'] ??
        item['园区地区生产总值'],
    ),
    grossRegionalProductYear: toNullableInteger(
      item.grossRegionalProductYear ?? item['园区地区生产总值年份'],
    ),
    grossRegionalProductStatement: pickFirstNonEmptyString(item, [
      'grossRegionalProductStatement',
      '园区地区生产总值原始表述',
    ]),
    grossRegionalProductScope: pickFirstNonEmptyString(item, [
      'grossRegionalProductScope',
      '园区地区生产总值统计口径',
    ]),
    grossRegionalProductSource: pickFirstNonEmptyString(item, [
      'grossRegionalProductSource',
      '园区地区生产总值来源',
    ]),
    grossRegionalProductSourceUrl: pickFirstNonEmptyString(item, [
      'grossRegionalProductSourceUrl',
      '园区地区生产总值来源链接',
    ]),
    grossRegionalProductVerificationStatus: normalizeGdpVerificationStatus(
      item.grossRegionalProductVerificationStatus ??
        item['园区地区生产总值核验状态'],
    ),
  };
}

function pickFirstNonEmptyString(
  item: Record<string, unknown>,
  keys: string[],
): string {
  for (const k of keys) {
    const v = item[k];
    const s = toTrimString(v);
    if (s) return s;
  }
  return '';
}

function pickLeadingIndustries(item: Record<string, unknown>): string[] {
  const v = item['leadingIndustries'];
  if (Array.isArray(v)) {
    return flattenLabeledMultiValues(v, LEADING_INDUSTRY_SPLIT);
  }
  return splitLabeledMultiValue(item['主导产业类型'], LEADING_INDUSTRY_SPLIT);
}

function pickProjectTypes(item: Record<string, unknown>): string[] {
  const v = item['projectTypes'];
  if (Array.isArray(v)) {
    return v.map((x) => toTrimString(x)).filter(Boolean);
  }
  return splitMultiCell(item['可建设落地项目类型']);
}

function pickTechnicalAdvantages(item: Record<string, unknown>): string[] {
  const v = item['technicalAdvantages'];
  if (Array.isArray(v)) {
    return v.map((x) => toTrimString(x)).filter(Boolean);
  }
  return splitMultiCell(item['技术优势']);
}

/** 区：兼容 JSON 中字符串或数组（如暂无区县时为空数组） */
function pickDistrict(record: Record<string, unknown>): string {
  const v = record.district ?? record['区'];
  if (Array.isArray(v)) {
    return v
      .map((x) => toTrimString(x))
      .filter(Boolean)
      .join('、');
  }
  return toTrimString(v);
}

function normalizeNationalPark(item: any, _index: number): NationalPark {
  const record = item as Record<string, unknown>;
  const name = pickFirstNonEmptyString(record, ['name', '园区名称']);
  const province = pickFirstNonEmptyString(record, ['province', '省份']);
  const city = pickFirstNonEmptyString(record, ['city', '市']);
  const district = pickDistrict(record);

  return {
    parkLevel: 'national',
    id: buildStableParkId('national', [province, city, district, name]),
    name,
    province,
    city,
    district,
    file: pickFirstNonEmptyString(record, ['file', '文件']),
    range: pickFirstNonEmptyString(record, ['range', '范围']),
    buildingCycle: pickFirstNonEmptyString(record, ['buildingCycle', '建设周期']),
    dockingStatus: pickFirstNonEmptyString(record, ['dockingStatus', '对接情况']),
    projectTypes: pickProjectTypes(record),
    technicalAdvantages: pickTechnicalAdvantages(record),
    investmentBracket: pickFirstNonEmptyString(record, [
      'investmentBracket',
      '投资额',
      '投资额（亿元）',
      '投资额(亿元)',
      '投资金额',
    ]),
    basicSituation: pickFirstNonEmptyString(record, ['basicSituation', '基本情况']),
    longitude: toNumberOrZero(record.longitude ?? record['经度']),
    latitude: toNumberOrZero(record.latitude ?? record['纬度']),
  };
}

function normalizeProvincialLikePark(
  item: any,
  _index: number,
): ProvincialLikePark {
  const record = item as Record<string, unknown>;

  const rawLevel = toTrimString(record.parkLevel ?? record['园区等级']);
  const parkLevel: ProvincialLikePark['parkLevel'] =
    rawLevel === 'pending' || rawLevel === '待开发' || rawLevel === '待开发 '
      ? 'pending'
      : 'provincial';

  if (parkLevel === 'pending') {
    const approval: PendingPark['pendingApproval'] =
      record.pendingApproval === 'state_council' ||
      record.pendingApproval === 'provincial_government'
        ? record.pendingApproval
        : normalizePendingApproval(record['批准机关']);
    const name = pickFirstNonEmptyString(record, ['name', '园区名称']);
    const province = pickFirstNonEmptyString(record, ['province', '省份']);
    const city = pickFirstNonEmptyString(record, ['city', '市']);
    const district = pickFirstNonEmptyString(record, ['district', '区']);

    return {
      parkLevel: 'pending',
      pendingApproval: approval,
      id: buildStableParkId('pending', [approval, province, city, district, name]),
      name,
      province,
      city,
      district,
      file: pickFirstNonEmptyString(record, ['file', '文件']),
      dockingStatus: pickFirstNonEmptyString(record, ['dockingStatus', '对接情况']),
      hasGreenElectricity: ['是', '有', 'true', '1'].includes(
        toTrimString(record.hasGreenElectricity ?? record['是否有绿电资源'])
          .toLowerCase(),
      ),
      greenElectricityScale: pickFirstNonEmptyString(
        record,
        ['greenElectricityScale', '绿电资源规模'],
      ),
      leadingIndustries: pickLeadingIndustries(record),
      comprehensiveEnergyConsumption: pickFirstNonEmptyString(
        record,
        ['comprehensiveEnergyConsumption', '综合能源消费量'],
      ),
      ...pickPendingGdpMetadata(record),
      basicSituation: pickFirstNonEmptyString(record, [
        'basicSituation',
        '园区基本情况',
      ]),
      failReason: pickFirstNonEmptyString(record, [
        'failReason',
        '申报失败原因',
      ]),
      remark: pickFirstNonEmptyString(record, ['remark', '备注']),
      longitude: toNumberOrZero(record.longitude ?? record['经度']),
      latitude: toNumberOrZero(record.latitude ?? record['纬度']),
    };
  }

  const name = pickFirstNonEmptyString(record, ['name', '园区名称']);
  const province = pickFirstNonEmptyString(record, ['province', '省份']);
  const city = pickFirstNonEmptyString(record, ['city', '市']);
  const district = pickFirstNonEmptyString(record, ['district', '区']);

  return {
    parkLevel: 'provincial',
    id: buildStableParkId('provincial', [province, city, district, name]),
    name,
    province,
    city,
    district,
    file: pickFirstNonEmptyString(record, ['file', '文件']),
    dockingStatus: pickFirstNonEmptyString(record, ['dockingStatus', '对接情况']),
    hasGreenElectricity: ['是', '有', 'true', '1'].includes(
      toTrimString(record.hasGreenElectricity ?? record['是否有绿电资源']).toLowerCase(),
    ),
    greenElectricityScale: pickFirstNonEmptyString(
      record,
      ['greenElectricityScale', '绿电资源规模'],
    ),
    leadingIndustries: pickLeadingIndustries(record),
    comprehensiveEnergyConsumption: pickFirstNonEmptyString(
      record,
      ['comprehensiveEnergyConsumption', '综合能源消费量'],
    ),
    basicSituation: pickFirstNonEmptyString(record, ['basicSituation', '园区基本情况']),
    failReason: pickFirstNonEmptyString(record, ['failReason', '申报失败原因']),
    remark: pickFirstNonEmptyString(record, ['remark', '备注']),
    longitude: toNumberOrZero(record.longitude ?? record['经度']),
    latitude: toNumberOrZero(record.latitude ?? record['纬度']),
  };
}

async function loadMockFromPublicJson(): Promise<{
  national: NationalPark[];
  provincial: ProvincialLikePark[];
}> {
  if (mockCache) return mockCache;

  const [nationalJson, provincialJson, pendingJson] =
    await Promise.all([
      fetchJson(MOCK_NATIONAL_JSON),
      fetchJson(MOCK_PROVINCIAL_JSON),
      fetchJson(MOCK_PENDING_JSON),
    ]);

  const national: NationalPark[] = nationalJson.map((item: any, i: number) =>
    normalizeNationalPark(item, i),
  );

  // 1) 省级（provincialParks.json）里可能仍保留“待开发”，这里统一剔除，避免重复/混淆
  const provincialBase: ProvincialLikePark[] = (provincialJson as any[])
    .filter((item: Record<string, unknown>) => !isJsonRowPending(item))
    .map((item: Record<string, unknown>) => {
      return mapJsonToProvincialLike(
        item,
        'provincial',
      );
    });

  // 2) 待开发（pending-*.json）为“单独数据源”，不依赖 provincialParks.json 是否包含园区等级字段
  const pendingParks: ProvincialLikePark[] = (pendingJson as any[]).map(
    (item: Record<string, unknown>) => {
      return mapJsonToProvincialLike(
        item,
        'pending',
        normalizePendingApproval(item['批准机关']),
      );
    },
  );

  const provincial: ProvincialLikePark[] = [
    ...provincialBase,
    ...pendingParks,
  ];

  mockCache = { national, provincial };
  return mockCache;
}

export function clearParksMockCache(): void {
  mockCache = null;
}

async function fetchAllParksRemote(): Promise<{
  national: NationalPark[];
  provincial: ProvincialLikePark[];
}> {
  const url = `${API_BASE}/parks`;
  const res = await fetch(url, { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error(`获取园区列表失败 ${res.status}`);
  const data = await res.json();
  if (!Array.isArray(data.national) || !Array.isArray(data.provincial)) throw new Error('响应格式异常');
  return {
    national: data.national.map((x: any, i: number) => normalizeNationalPark(x, i)),
    provincial: data.provincial.map((x: any, i: number) =>
      normalizeProvincialLikePark(x, i),
    ),
  };
}

export async function fetchAllParks(): Promise<{
  national: NationalPark[];
  provincial: ProvincialLikePark[];
}> {
  if (USE_REMOTE_API) return fetchAllParksRemote();
  return loadMockFromPublicJson();
}

function splitFilteredByLevel(parks: Park[]): {
  national: NationalPark[];
  provincial: ProvincialLikePark[];
} {
  const national: NationalPark[] = [];
  const provincial: ProvincialLikePark[] = [];
  for (const p of parks) {
    if (isNationalPark(p)) national.push(p);
    else if (isProvincialPark(p) || isPendingPark(p)) provincial.push(p);
  }
  return { national, provincial };
}

async function fetchParksByFilterRemote(
  filter: FilterCondition,
): Promise<{ national: NationalPark[]; provincial: ProvincialLikePark[] }> {
  const url = `${API_BASE}/parks/filter`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(filter),
  });
  if (!res.ok) throw new Error(`筛选失败 ${res.status}`);
  const data = await res.json();
  const national = Array.isArray(data.national) ? data.national : [];
  const provincial = Array.isArray(data.provincial) ? data.provincial : [];
  return {
    national: national.map((x: any, i: number) => normalizeNationalPark(x, i)),
    provincial: provincial.map((x: any, i: number) => normalizeProvincialLikePark(x, i)),
  };
}

export async function fetchParksByFilter(
  filter: FilterCondition,
): Promise<{ national: NationalPark[]; provincial: ProvincialLikePark[] }> {
  if (USE_REMOTE_API) return fetchParksByFilterRemote(filter);
  const { national, provincial } = await loadMockFromPublicJson();
  const combined = filterParksByCondition([...national, ...provincial] as Park[], filter);
  return splitFilteredByLevel(combined);
}

async function fetchParkDetailRemote(parkId: string): Promise<Park> {
  const url = `${API_BASE}/parks/${encodeURIComponent(parkId)}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`详情请求失败 ${res.status}`);
  const raw = await res.json();
  // 后端可能返回已归一化对象，也可能是“中文键原始数据”，这里尽量兼容，保证 DetailPanel 不丢字段。
  if (raw && raw.parkLevel === 'national') return normalizeNationalPark(raw, 0);
  if (raw && (raw.parkLevel === 'provincial' || raw.parkLevel === 'pending'))
    return normalizeProvincialLikePark(raw, 0);
  // 根据可能存在的中文字段判断
  const levelRaw = toTrimString(raw?.parkLevel ?? raw?.['园区等级']);
  if (levelRaw === 'pending' || levelRaw === '待开发') return normalizeProvincialLikePark(raw, 0);
  if (levelRaw === 'provincial') return normalizeProvincialLikePark(raw, 0);
  // 默认降级：当作省级展示
  return normalizeProvincialLikePark(raw, 0);
}

export async function fetchParkDetail(parkId: string): Promise<Park> {
  if (!parkId.trim()) return Promise.reject(new Error('parkId 不能为空'));
  if (USE_REMOTE_API) return fetchParkDetailRemote(parkId);
  const { national, provincial } = await loadMockFromPublicJson();
  const found =
    national.find((p) => p.id === parkId) ??
    provincial.find((p) => p.id === parkId);
  if (!found) throw new Error(`未找到 id 为 ${parkId} 的园区`);
  return found;
}
